import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
// Playfair Display is served from mattdigging.com itself (no request to Google Fonts).
import '@fontsource/playfair-display/400.css';
import '@fontsource/playfair-display/400-italic.css';
import '@fontsource/playfair-display/600.css';
import './index.scss';
import Lenis from '@studio-freight/lenis';

// Pages
import Home from './pages/Home/Home';
import About from './pages/About/About';
import Artists from './pages/Artists/Artists';
import Playlists from './pages/Playlists/Playlists';
import Selections from './pages/Selections/Selections';
import AuthConfirm from './pages/AuthConfirm/AuthConfirm';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import Admin from './pages/Admin/Admin';
import Dashboard from './pages/Dashboard/Dashboard';
import RequireAuth from './components/RequireAuth/RequireAuth';
import HiddenGems from './pages/HiddenGems/HiddenGems';
import Terms from './pages/Terms/Terms';
import Privacy from './pages/Privacy/Privacy';
import AppTest from './pages/AppTest/AppTest';
import Support from './pages/Support/Support';
import Profile from './pages/Profile/Profile';
import Membership from './pages/Membership/Membership';
import Login from './pages/Login/Login';
import Landing from './pages/Landing/Landing';

// Components
import Player from './components/Player/Player';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import MainWrapper from './components/MainWrapper/MainWrapper';
import Modals from './components/Modals/Modals';

// Utils
import { AppProvider } from './context/AppContext';
import reportWebVitals from './reportWebVitals';
import { IS_LANDING_SITE, PLAY_URL } from './lib/site';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';

export let lenis: Lenis;

// On mattdigging.com, any page that isn't part of the landing site belongs to the web app.
// Keep the path, query and hash so email links (/auth/confirm#access_token=…) still work.
function RedirectToPlay() {
  const location = useLocation();
  useEffect(() => {
    window.location.replace(`${PLAY_URL}${location.pathname}${location.search}${location.hash}`);
  }, [location]);
  return null;
}

// A login coming back from Google (or an email link) that Supabase sent to its Site URL,
// mattdigging.com, instead of the web app: "#access_token=…", "?code=…" or an auth error.
// Pass it straight on to the web app, tokens included, before anything renders (the Supabase
// client would otherwise read and clear them here, on the wrong site).
if (
  IS_LANDING_SITE &&
  /(^|[#&?])(access_token|code|error_description)=/.test(window.location.hash + window.location.search)
) {
  window.location.replace(`${PLAY_URL}${window.location.pathname}${window.location.search}${window.location.hash}`);
}

// mattdigging.com: landing page + legal, support and tester pages (see lib/site.ts).
function LandingSite() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/support" element={<Support />} />
      <Route path="/android" element={<AppTest platform="android" />} />
      <Route path="/ios" element={<AppTest platform="ios" />} />
      <Route path="*" element={<RedirectToPlay />} />
    </Routes>
  );
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

function App() {
  const location = useLocation();
  const isTermsPage = location.pathname === '/terms';

  useEffect(() => {
    if (location.pathname === '/admin' || location.pathname === '/dashboard') return;

    const lenis = new Lenis({
      duration: 1.2,
      smoothWheel: true,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, [location.pathname]);

  if (IS_LANDING_SITE) return <LandingSite />;

  return (
    <Routes>
      {/* Public route for email confirmation */}
      <Route path="/auth/confirm" element={<AuthConfirm />} />

      {/* Public route for password reset */}
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      {/* Support URL for the App Store and Google Play */}
      <Route path="/support" element={<Support />} />
      {/* Short links for app testers */}
      <Route path="/android" element={<AppTest platform="android" />} />
      <Route path="/ios" element={<AppTest platform="ios" />} />

      {/* Admin route - separate layout */}
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <Admin />
          </RequireAuth>
        }
      />

      {/* Dashboard route - password-gated, separate layout */}
      <Route path="/dashboard" element={<Dashboard />} />

      {/* Login route - separate layout */}
      {/* <Route
        path="/login"
        element={
          <RequireAuth>
            <Login />
          </RequireAuth>
        }
      /> */}

      {/* All other routes with main layout */}
      <Route
        path="*"
        element={
          <RequireAuth>
            <Header />
            <main className="main">
              {!isTermsPage && (
                <MainWrapper>
                  <Home />
                </MainWrapper>
              )}

              {!isTermsPage && <Player />}
              {!isTermsPage && <Modals />}

              <Routes>
                <Route path="/" element={<></>} />
                <Route path="/hidden-gems" element={<HiddenGems />} />
                <Route path="/about" element={<About />} />
                <Route path="/artists" element={<Artists />} />
                <Route path="/playlists" element={<Playlists />} />
                <Route path="/selections" element={<Selections />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/membership" element={<Membership />} />
              </Routes>
            </main>

            <Routes>
              <Route path="/login" element={<Login />} />
            </Routes>
            
            <Footer />
          </RequireAuth>
        }
      />
    </Routes>
  );
}

root.render(
  <React.StrictMode>
    <AppProvider>
      <Router>
        <App />
      </Router>
    </AppProvider>
  </React.StrictMode>
);

reportWebVitals();